'use client';

import React, { useEffect, useRef } from 'react';
import { useFormContext, useFieldArray, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import InfoText from '@/components/atoms/InfoText';
import type { CategoryAttribute, PermutationSlot, VariantRow } from '../_hooks/useVariantPermutations';
import { syncVariantsToPermutations } from '../_hooks/useVariantPermutations';

interface VariantsTableProps {
  attributes: CategoryAttribute[];
  currency: string;
  isCart: boolean;
  applyPricingTiers: boolean;
  attributesLoading?: boolean;
  /** Computed cartesian product permutations from the parent (SkuInventoryForm). */
  permutations: PermutationSlot[][];
  exchangeRate: number;
  preferredCurrency: string;
}

// Sub-component so useWatch re-renders only the affected cell
const StockCell = ({ index }: { index: number }) => {
  const { control, register, getValues, setValue } = useFormContext();
  const stock = useWatch({ control, name: `variants.${index}.stock` });
  const isZero = !stock || Number(stock) <= 0;
  return (
    <div
      className="flex items-center justify-start gap-0.5 w-full"
      data-testid={`variants-row-${index}-stock-wrapper`}
    >
      <input
        type="number"
        {...register(`variants.${index}.stock`, { valueAsNumber: true })}
        min={0}
        onKeyDown={(e) => {
          if (['-', '+', 'e', 'E', '.'].includes(e.key)) {
            e.preventDefault();
          }
        }}
        onInput={(e) => {
          let val = e.currentTarget.value;
          if (val.startsWith('-')) {
            val = val.replace('-', '');
          }
          val = val.replace(/^0+(?=\d)/, '');
          if (val !== e.currentTarget.value) {
            e.currentTarget.value = val;
          }
          e.currentTarget.style.width = `${Math.max(1, e.currentTarget.value.length + 0.5)}ch`;
        }}
        className={`min-w-0 p-0 border-none bg-transparent focus:ring-0 text-sm !font-normal leading-none outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${isZero ? 'text-amber-500 !font-bold' : 'text-light-text-primary dark:text-dark-text-primary'
          }`}
        data-testid={`variants-${index}-stock-input`}
      />
      <div className="flex flex-col gap-[2px] flex-shrink-0" data-testid={`variants-${index}-stock-controls`}>
        <button
          type="button"
          onClick={() => {
            const newVal =
              Number(getValues(`variants.${index}.stock`) || 0) + 1;
            setValue(`variants.${index}.stock`, newVal, { shouldDirty: true });
          }}
          className="p-1 text-light-text-primary dark:text-dark-text-primary hover:text-black dark:hover:text-white"
          data-testid={`variants-${index}-stock-increment`}
        >
          <svg width="12" height="7" viewBox="0 0 8 5" fill="currentColor">
            <path d="M4 0L8 5H0L4 0Z" />
          </svg>
        </button>
        <button
          type="button"
          onClick={() => {
            const newVal = Math.max(
              0,
              Number(getValues(`variants.${index}.stock`) || 0) - 1
            );
            setValue(`variants.${index}.stock`, newVal, { shouldDirty: true });
          }}
          className="px-1 text-neutral-800 dark:text-neutral-200 hover:text-black dark:hover:text-white"
          data-testid={`variants-${index}-stock-decrement`}
        >
          <svg width="12" height="7" viewBox="0 0 8 5" fill="currentColor">
            <path d="M4 5L0 0H8L4 5Z" />
          </svg>
        </button>
      </div>
    </div>
  );
};

// Sub-component for price — turns red when value is 0
const PriceCell = ({
  index,
  exchangeRate,
}: {
  index: number;
  exchangeRate: number;
}) => {
  const { control, register, getValues, setValue } = useFormContext();
  const price = useWatch({ control, name: `variants.${index}.price` });
  const isFree = !price || Number(price) <= 0;

  useEffect(() => {
    const convertedPrice = price ? Number(price) * exchangeRate : 0;
    setValue(`variants.${index}.priceConverted`, convertedPrice, { shouldDirty: true });
  }, [price, exchangeRate, setValue, index]);

  return (
    <div
      className="flex items-center justify-start gap-1 w-full"
      data-testid={`variants-row-${index}-price-wrapper`}
    >
      <div className="flex items-center gap-1 min-w-0" data-testid={`variants-row-${index}-price-inner`}>
        <input
          type="number"
          {...register(`variants.${index}.price`, { valueAsNumber: true })}
          min={0}
          onKeyDown={(e) => {
            if (['-', '+', 'e', 'E'].includes(e.key)) {
              e.preventDefault();
            }
          }}
          onInput={(e) => {
            let val = e.currentTarget.value;
            if (val.startsWith('-')) {
              val = val.replace('-', '');
            }
            val = val.replace(/^0+(?=\d)/, '');
            if (val !== e.currentTarget.value) {
              e.currentTarget.value = val;
            }
            e.currentTarget.style.width = `${Math.max(1, e.currentTarget.value.length + 0.5)}ch`;
          }}
          className={`min-w-0 p-0 border-none bg-transparent focus:ring-0 text-sm !font-normal leading-none outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${isFree ? 'text-red-500 !font-bold' : 'text-light-text-primary dark:text-dark-text-primary'
            }`}
          data-testid={`variants-${index}-price-input`}
        />
      </div>
      <div className="flex flex-col gap-[2px] flex-shrink-0" data-testid={`variants-${index}-price-controls`}>
        <button
          type="button"
          onClick={() => {
            const newVal =
              Number(getValues(`variants.${index}.price`) || 0) + 1;
            setValue(`variants.${index}.price`, newVal, { shouldDirty: true });
          }}
          className="px-1 text-neutral-800 dark:text-neutral-200 hover:text-black dark:hover:text-white"
          data-testid={`variants-${index}-price-increment`}
        >
          <svg width="12" height="7" viewBox="0 0 8 5" fill="currentColor">
            <path d="M4 0L8 5H0L4 0Z" />
          </svg>
        </button>
        <button
          type="button"
          onClick={() => {
            const newVal = Math.max(
              0,
              Number(getValues(`variants.${index}.price`) || 0) - 1
            );
            setValue(`variants.${index}.price`, newVal, { shouldDirty: true });
          }}
          className="p-1 text-neutral-800 dark:text-neutral-200 hover:text-black dark:hover:text-white"
          data-testid={`variants-${index}-price-decrement`}
        >
          <svg width="12" height="7" viewBox="0 0 8 5" fill="currentColor">
            <path d="M4 5L0 0H8L4 5Z" />
          </svg>
        </button>
      </div>
    </div>
  );
};

// Sub-component for priceConverted — preferred currency live preview (read-only).
// While editing, derived client-side from the SAR input × exchange rate.
// On GET responses, the backend returns priceConverted directly.
const PriceConvertedCell = ({
  index,
}: {
  index: number;
}) => {
  const { control } = useFormContext();
  const priceConverted = useWatch({ control, name: `variants.${index}.priceConverted` });
  const value = priceConverted ? Number(priceConverted) : 0;
  return (
    <div
      className="flex items-center justify-start gap-1 w-full"
      data-testid={`variants-row-${index}-price-converted-wrapper`}
    >
      <div className="!text-sm !text-[#090909] dark:text-neutral-500 !font-normal px-1">
        {value.toLocaleString(undefined, { maximumFractionDigits: 2 })}
      </div>
    </div>
  );
};

// ─── VariantsTable ─────────────────────────────────────────────────────────────
// Pure render + single source of truth for the variants field array.
//
// The parent (SkuInventoryForm) computes the cartesian product permutations from
// the current attribute/color selections and passes them as `permutations` prop.
// On the first render (edit/duplicate hydration) we skip the sync so existing
// stock/price/sku data is preserved. Every subsequent change is user-driven.
export const VariantsTable = ({
  attributes,
  // _currency,
  isCart,
  applyPricingTiers,
  attributesLoading,
  permutations,
  exchangeRate,
  preferredCurrency,
}: VariantsTableProps) => {
  const { t: t } = useTranslation();
  const { t: tValidations } = useTranslation();
  const { control, getValues } = useFormContext();

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'variants',
  });

  // ── Sync permutations → variant rows ────────────────────────────────────────
  // Guards against syncing before attributes have loaded.
  // Initialized to true if attributes are pre-loaded (Add mode),
  // false if still pending (Edit mode).
  const wasLoadingRef = useRef(!attributesLoading && attributes.length > 0);

  useEffect(() => {
    if (attributesLoading) {
      wasLoadingRef.current = true;
      return;
    }
    if (!wasLoadingRef.current) return;

    syncVariantsToPermutations(
      permutations,
      (getValues('variants') as VariantRow[]) ?? [],
      append,
      remove
    );
    // `append`, `remove`, and `getValues` are stable refs from useFieldArray / useFormContext.
    // We intentionally only re-run when permutations or loading state changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permutations, attributesLoading]);

  if (fields.length === 0) return null;

  const showPrice = isCart || !applyPricingTiers;

  const firstField = fields[0] as unknown as VariantRow;
  const columnHeaders: PermutationSlot[] = firstField?._displayDetails ?? [];

  return (
    <div
      className="w-full flex flex-col items-center"
      data-testid="variants-table-wrapper"
    >
      <div
        className="mt-2 w-full border border-gray-200 dark:border-neutral-800 !rounded-xl bg-white dark:bg-dark-surface-secondary overflow-hidden"
        data-testid="variants-table-container"
      >
        <div className="overflow-x-auto" data-testid="variants-table-scroll">
          <table
            className="w-full table-auto sm:table-fixed text-sm text-left border-collapse"
            data-testid="variants-table"
          >
            <thead
              className="text-xs !font-semibold !text-light-text-primary bg-white dark:bg-dark-surface-secondary dark:text-neutral-400"
              data-testid="variants-table-head"
            >
              <tr data-testid="variants-table-head-row">
                {columnHeaders.map((slot, idx) => {
                  const attrName =
                    slot.attrId === 'color'
                      ? t('colorLabel')
                      : attributes.find((a) => a.id === slot.attrId)?.name ||
                      t('variantsAttribute');
                  return (
                    <th
                      key={idx}
                      className="px-4 py-3 !font-semibold whitespace-nowrap border border-gray-200 dark:border-neutral-800"
                      data-testid={`variants-col-header-${idx}`}
                    >
                      {attrName}
                    </th>
                  );
                })}
                <th
                  className="px-4 py-3 !font-semibold whitespace-nowrap border border-gray-200 dark:border-neutral-800"
                  data-testid="variants-col-sku"
                >
                  <div
                    className="flex flex-col"
                    data-testid="variants-col-sku-header"
                  >
                    <InfoText
                      text={t('skuId')}
                      size="xs"
                      className="!font-semibold !text-light-text-primary dark:!text-neutral-400"
                    />
                  </div>
                </th>
                <th
                  className="px-4 py-3 !font-semibold whitespace-nowrap border border-gray-200 dark:border-neutral-800"
                  data-testid="variants-col-stock"
                >
                  <div className="flex items-center gap-1">
                    <span>{t('stock')}</span>
                    <InfoText
                      text={tValidations('optionalHeader')}
                      size="xs"
                      className="!font-normal !text-amber-500"
                    />
                  </div>
                </th>
                {showPrice && (
                  <>
                    <th
                      className="ps-1 pe-6 py-3 !font-semibold whitespace-nowrap border border-gray-200 dark:border-neutral-800"
                      data-testid="variants-col-price"
                    >
                      <div className="flex items-start">
                        <span>{t('priceInSar')}</span>
                        <InfoText
                          text={tValidations('requiredHeader')}
                          className="!font-normal !text-[11px] !text-red-500"
                        />
                      </div>
                    </th>
                    {preferredCurrency !== 'SAR' && (
                      <th
                        className="p-4 !font-semibold whitespace-nowrap border border-gray-200 dark:border-neutral-800"
                        data-testid="variants-col-price-converted"
                      >
                        <div className="flex items-center gap-2">
                          <span>{t('priceInCurrency', { currency: preferredCurrency })}</span>
                        </div>
                      </th>
                    )}
                  </>
                )}
              </tr>
            </thead>

            <tbody
              className="divide-y divide-gray-200 dark:divide-neutral-800"
              data-testid="variants-table-body"
            >
              {(fields as unknown as VariantRow[]).map((field, index) => (
                <tr
                  key={field._permutationKey}
                  className="bg-white dark:bg-dark-surface-primary hover:bg-gray-50 dark:hover:bg-dark-surface-secondary/50 transition-colors"
                  data-testid={`variants-row-${index}`}
                >
                  {/* Dynamic Attributes */}
                  {field._displayDetails?.map((slot, idx) => (
                    <td
                      key={idx}
                      className="px-4 py-4 whitespace-nowrap text-sm !font-normal leading-none text-light-text-primary dark:text-dark-text-primary border border-gray-200 dark:border-neutral-800"
                      data-testid={`variants-row-${index}-attr-${idx}`}
                    >
                      {slot.detail.name}
                    </td>
                  ))}

                  {/* SKU Column — read-only, backend-assigned. Displays '-' when null. */}
                  <td
                    className="px-4 py-4 whitespace-nowrap border border-gray-200 dark:border-neutral-800 text-sm text-light-text-primary dark:text-dark-text-primary !font-normal leading-none"
                    data-testid={`variants-row-${index}-sku-cell`}
                  >
                    <span data-testid={`variants-${index}-sku`}>
                      {field.sku ?? '-'}
                    </span>
                  </td>

                  {/* Stock Column */}
                  <td
                    className="p-4 whitespace-nowrap border border-gray-200 dark:border-neutral-800"
                    data-testid={`variants-row-${index}-stock-cell`}
                  >
                    <StockCell index={index} />
                  </td>

                  {/* Price Columns */}
                  {showPrice && (
                    <>
                      <td
                        className="p-4 whitespace-nowrap border border-gray-200 dark:border-neutral-800"
                        data-testid={`variants-row-${index}-price-cell`}
                      >
                        <PriceCell index={index} exchangeRate={exchangeRate} />
                      </td>
                      {preferredCurrency !== 'SAR' && (
                        <td
                          className="p-4 whitespace-nowrap border border-gray-200 dark:border-neutral-800 bg-gray-50 dark:bg-dark-surface-secondary"
                          data-testid={`variants-row-${index}-price-converted-cell`}
                        >
                          <PriceConvertedCell index={index} />
                        </td>
                      )}
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
